import { createSelector, Selector } from 'reselect';

import { RootState } from '@/model/types';

import { AnyTransactionPeriod, Transaction, Wallet, WalletId } from './types';

export const makeSelectInitialized = (): Selector<RootState, boolean> =>
  createSelector(
    (state: RootState) => state.wallets,
    (state) => state.initialized,
  );

export const makeSelectSelectedWalletsBalance = (): Selector<RootState, number> =>
  createSelector(
    (state: RootState) => state.wallets.wallets,
    (wallets) => wallets.reduce((result, { balance: { amount } }) => result + amount, 0) || 0,
  );

export const makeSelectWallets = (): Selector<
  RootState,
  {
    all: Wallet[];
    selected?: WalletId;
  }
> =>
  createSelector(
    (state: RootState) => state.wallets.wallets,
    (state: RootState) => state.wallets.selectedWallet,
    (all, selected) => ({
      all,
      selected,
    }),
  );

export const makeSelectWallet = (id: WalletId): Selector<RootState, Wallet | undefined> =>
  createSelector(makeSelectWallets(), (state) => state.all.find(({ id: saved }) => saved === id));

export const makeSelectDefaultWallet = (): Selector<RootState, Wallet | undefined> =>
  createSelector(makeSelectWallets(), (state) => state.all.find(({ id }) => id === state.selected) || state.all[0]);

export const makeSelectTransactionsFilter = (): Selector<RootState, AnyTransactionPeriod> =>
  createSelector(
    (state: RootState) => state.wallets,
    (state) => state.transactionsFilter,
  );

export const makeSelectTransactions = (): Selector<
  RootState,
  { hasMore: boolean; data: Transaction[]; lastPage: number } | undefined
> =>
  createSelector(
    (state: RootState) => state.wallets,
    (state) => state.transactions,
  );

export const makeSelectTransaction = (id: number): Selector<RootState, Transaction | undefined> =>
  createSelector(
    (state: RootState) => state.wallets.transactions?.data || [],
    (state) => state.filter(({ transactionId }) => transactionId === id)[0],
  );

export const makeSelectLastReload = (type: 'balance' | 'transactions'): Selector<RootState, Date | undefined> =>
  createSelector(
    (state: RootState) => state.wallets.lastReload,
    (state) => (type === 'balance' ? state.balance : state.transactions),
  );

export default {
  makeSelectInitialized,
  makeSelectDefaultWallet,
  makeSelectWallet,
  makeSelectWallets,
  makeSelectSelectedWalletsBalance,
  makeSelectTransactions,
  makeSelectTransaction,
  makeSelectTransactionsFilter,
  makeSelectLastReload,
};
